import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Building2, Camera, CheckCircle2, FileText, ListChecks, Loader2, Send, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CompanyFields } from "@/pages/SafetySurvey";
import { STATE_LABEL } from "./FieldHome";
import { metaFieldsFor } from "./FieldNew";
import { useFieldSync } from "./FieldSync";
import { useFieldSurvey } from "./useFieldSurvey";

const eqName = (code) => (code || "").replace(/_/g, " ");
const BIG = "min-h-[64px] rounded-2xl border-2 px-5 text-lg font-bold disabled:opacity-40";
const PRIMARY = `${BIG} border-[#0D4D4F] bg-[#0D4D4F] text-white`;
const SECOND = `${BIG} border-[#0D4D4F] bg-white text-[#0D4D4F]`;
const GHOST = `${BIG} border-[#D9CBB4] bg-transparent text-[#5C6667] font-semibold`;

// ------------------------------------------------------------------ una domanda per schermata
function FieldQuestion({ q, taskName, busy, onAnswer, onPhoto, editing, onCancel }) {
  const isMulti = q.answer_type === "multi_choice" || q.answer_type === "equipment_multi";
  const isNum = q.answer_type === "number" || q.answer_type === "duration_min";
  const initial = () => (q.state === "answered" && q.value != null ? q.value : isMulti ? [] : isNum ? 0 : "");
  const [value, setValue] = useState(initial);
  const [evidence, setEvidence] = useState(q.evidence_ids || []);
  useEffect(() => { setValue(initial()); setEvidence(q.evidence_ids || []); }, [q.key, q.answer_type]);
  const answer = (state, val) => onAnswer(q, state, val, evidence);
  const toggle = (v) => setValue((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  const needsPhoto = q.evidence_requirements.length > 0 && evidence.length === 0 && q.state !== "no";
  return (
    <div className="rounded-3xl border border-[#D9CBB4] bg-white p-5 shadow-sm" data-testid="field-question">
      <p className="mb-2 text-sm text-[#5C6667]">
        <span className="rounded-full bg-[#EFE6D8] px-3 py-1 font-semibold text-[#1E2425]">{q.task_code ? taskName(q.task_code) : "Generale"}</span>
        {q.equipment_code && <span className="ml-2 rounded-full bg-[#EFE6D8] px-3 py-1 font-semibold">{eqName(q.equipment_code)}</span>}
      </p>
      <h2 className="mb-3 text-2xl font-bold leading-tight">{q.text}</h2>
      {q.help_text && <p className="mb-4 rounded-xl bg-[#EFE6D8] p-3 text-base">{q.help_text}</p>}
      {q.critical && <p className="mb-4 rounded-xl bg-[#F6E4E6] p-3 text-base text-[#5b1019]"><b>Domanda importante.</b> Se non sei sicuro, scegli «Non so»: verrà segnalata al consulente.</p>}

      {q.answer_type === "yes_no_unknown" && (
        <div className="grid grid-cols-2 gap-3">
          <button className={`${PRIMARY} min-h-[84px] text-3xl`} disabled={busy} onClick={() => answer("yes")}>Sì</button>
          <button className={`${SECOND} min-h-[84px] text-3xl`} disabled={busy} onClick={() => answer("no")}>No</button>
        </div>)}

      {q.answer_type === "single_choice" && (
        <div className="grid gap-2">
          {q.options.map((o) => (
            <button key={o.value} onClick={() => setValue(o.value)} className={`min-h-[64px] rounded-2xl border-2 px-4 text-left text-lg ${value === o.value ? "border-[#0D4D4F] bg-[#E3EEEE] font-bold" : "border-[#D9CBB4] bg-white"}`}>{o.label}</button>))}
        </div>)}

      {isMulti && (
        <div className="grid gap-2 sm:grid-cols-2">
          {q.options.map((o) => (
            <button key={o.value} onClick={() => toggle(o.value)} className={`flex min-h-[64px] items-center gap-3 rounded-2xl border-2 px-4 text-left text-lg ${value.includes(o.value) ? "border-[#0D4D4F] bg-[#E3EEEE] font-bold" : "border-[#D9CBB4] bg-white"}`}>
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg border-2 text-white ${value.includes(o.value) ? "border-[#0D4D4F] bg-[#0D4D4F]" : "border-[#5C6667] bg-white"}`}>{value.includes(o.value) ? "✓" : ""}</span>
              {o.label}
            </button>))}
        </div>)}

      {isNum && (
        <div className="flex items-center gap-3">
          <button className={`${SECOND} w-[72px] text-4xl`} onClick={() => setValue((v) => Math.max(0, Number(v || 0) - (q.answer_type === "duration_min" ? 15 : 1)))}>−</button>
          <Input type="number" inputMode="numeric" className="h-[72px] w-40 rounded-2xl border-2 border-[#0D4D4F] text-center text-4xl font-extrabold" value={value} onChange={(e) => setValue(e.target.value)} />
          <button className={`${SECOND} w-[72px] text-4xl`} onClick={() => setValue((v) => Number(v || 0) + (q.answer_type === "duration_min" ? 15 : 1))}>+</button>
          {q.answer_type === "duration_min" && <span className="text-lg text-[#5C6667]">minuti</span>}
        </div>)}

      {q.answer_type === "text" && <Textarea rows={3} className="rounded-2xl border-2 border-[#0D4D4F] p-4 text-xl" value={value} onChange={(e) => setValue(e.target.value)} />}

      {q.evidence_requirements.map((r) => (
        <label key={r.code} className={`mt-4 flex min-h-[64px] cursor-pointer items-center gap-3 rounded-2xl border-2 px-4 py-3 text-lg ${evidence.length ? "border-solid border-[#1F6B45] bg-[#DDF0E5]" : "border-dashed border-[#0D4D4F] bg-[#E3EEEE]"}`}>
          <Camera className="h-7 w-7 shrink-0" /> <span className="flex-1">{r.label}{evidence.length ? " · acquisita" : ""}</span>
          <input type="file" accept="image/*,application/pdf" capture={r.kind === "photo" ? "environment" : undefined} className="hidden"
            onChange={async (e) => { const f = e.target.files[0]; if (!f) return; const id = await onPhoto(q, r, f); if (id) { setEvidence((cur) => [...cur, id]); toast.success(`${r.label}: salvata`); } e.target.value = ""; }} />
        </label>))}
      {needsPhoto && <p className="mt-2 text-sm text-[#8A5A00]">Allega la prova richiesta prima di proseguire, se puoi. Senza prova il dato resta dichiarato.</p>}

      <div className="mt-5 flex flex-wrap gap-3">
        {q.answer_type !== "yes_no_unknown" && (
          <button className={PRIMARY} disabled={busy || (Array.isArray(value) ? !value.length : value === "")}
            onClick={() => answer("answered", isNum ? Number(value) : value)}>Avanti</button>)}
        <button className={GHOST} disabled={busy} onClick={() => answer("unknown")}>Non so</button>
        <button className={GHOST} disabled={busy} onClick={() => answer("not_verifiable")}>Non verificabile</button>
        {editing && <button className={GHOST} onClick={onCancel}>Annulla</button>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ fogli: dati, documenti, esemplari, risposte
function DocsSheet({ view, actions }) {
  const [dates, setDates] = useState({});
  const mark = (d, file) => {
    const expires_on = dates[d.key] || null;
    if (d.expiration_required && !expires_on) { toast.error("Indica prima la data di scadenza (o del prossimo controllo)"); return undefined; }
    return actions.document(d, file, expires_on).then(() => toast.success("Documento salvato"));
  };
  return (
    <div className="space-y-3">
      <p className="text-[#5C6667]">Documenti da chiedere o fotografare in azienda. Se non li hai con te, puoi inviare lo stesso: il consulente vedrà cosa manca.</p>
      {view.documents.length === 0 && <p>Nessun documento richiesto per ora.</p>}
      {view.documents.map((d) => (
        <div key={d.key} className="rounded-2xl border border-[#D9CBB4] bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="font-bold">{d.name}{d.subject ? ` (${d.subject.split(":").pop().replace("#", " · ").replace(/_/g, " ")})` : ""}</p>
            <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${d.status === "received" ? "bg-[#DDF0E5] text-[#1F6B45]" : d.status === "expired" ? "bg-[#F6E4E6] text-[#8B1E2D]" : "bg-[#FBEFD2] text-[#8A5A00]"}`}>
              {d.status === "received" ? "Acquisito" : d.status === "expired" ? "Scaduto" : "Da acquisire"}</span>
          </div>
          <p className="text-sm text-[#5C6667]">{d.category}{d.blocking ? " · serve per l'invio" : ""}{d.expires_on ? ` · scade il ${d.expires_on}` : ""}</p>
          {view.editable && d.status !== "received" && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {d.expiration_required && <label className="text-sm">Scadenza <Input type="date" className="mt-1 h-12 w-44 border-2" value={dates[d.key] || ""} onChange={(e) => setDates({ ...dates, [d.key]: e.target.value })} /></label>}
              <label className={`${SECOND} inline-flex cursor-pointer items-center gap-2`}><Camera className="h-5 w-5" /> Fotografa o carica
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && mark(d, e.target.files[0])} /></label>
            </div>)}
        </div>))}
    </div>
  );
}

function UnitsSheet({ view, actions }) {
  const types = Array.from(new Map(view.equipment_instances.map((i) => [`${i.task_code}:${i.equipment_code}`, i])).values());
  const toText = (key) => (view.equipment_units[key] || []).map((u) => [u.id, u.label && u.label !== u.id ? u.label : "", u.next_check || ""].join(" | ")).join("\n");
  const [text, setText] = useState({});
  const save = (i, key) => {
    const units = (text[key] ?? toText(key)).split("\n").map((r) => r.trim()).filter(Boolean).map((r) => {
      const [id, label, next_check] = r.split("|").map((x) => x.trim());
      return { id, label: label || null, next_check: next_check || null };
    });
    return actions.units(i.task_code, i.equipment_code, units).then(() => toast.success("Esemplari salvati"));
  };
  return (
    <div className="space-y-4">
      <p className="text-[#5C6667]">Se un'attrezzatura è presente in più esemplari, scrivine uno per riga: <b>matricola | nome | prossima verifica (AAAA-MM-GG)</b>. Ogni esemplare avrà i suoi documenti e la sua scadenza.</p>
      {types.length === 0 && <p>Nessuna attrezzatura indicata finora.</p>}
      {types.map((i) => {
        const key = `${i.task_code}:${i.equipment_code}`;
        return (
          <div key={key} className="rounded-2xl border border-[#D9CBB4] bg-white p-4">
            <p className="font-bold">{eqName(i.equipment_code)}</p>
            <Textarea rows={3} disabled={!view.editable} className="mt-2 border-2 text-base" placeholder="M1 | Martello Bosch | 2027-03-31" value={text[key] ?? toText(key)} onChange={(e) => setText({ ...text, [key]: e.target.value })} />
            {view.editable && <button className={`${SECOND} mt-2 min-h-[52px]`} onClick={() => save(i, key)}>Salva esemplari</button>}
          </div>);
      })}
    </div>
  );
}

function AnswersSheet({ view, onEdit }) {
  return (
    <div className="space-y-2">
      {view.answered.length === 0 && <p>Nessuna risposta ancora.</p>}
      {view.answered.map((a) => (
        <div key={a.key} className="flex items-center justify-between gap-3 rounded-2xl border border-[#D9CBB4] bg-white p-4">
          <div className="min-w-0"><p className="font-semibold">{a.text}</p>
            <p className="text-sm text-[#5C6667]">{a.state === "answered" ? (Array.isArray(a.value) ? a.value.join(", ") : String(a.value)) : a.state === "yes" ? "Sì" : a.state === "no" ? "No" : a.state.replace(/_/g, " ")}</p></div>
          {view.editable && <button className={`${SECOND} min-h-[48px] px-4 text-base`} onClick={() => onEdit(a)}>Modifica</button>}
        </div>))}
    </div>
  );
}

function DataSheet({ view, actions, catalog }) {
  const c = view.company || {};
  const [company, setCompany] = useState({ business_name: c.business_name || "", vat_number: c.vat_number || "", fiscal_code: c.fiscal_code || "",
    legal_address: c.legal_address || "", operating_address: c.operating_address || "", ateco_code: c.ateco_code || "", employees_total: c.employees_total ?? "", workers: c.workers || [] });
  const [meta, setMeta] = useState(view.meta || {});
  const fields = metaFieldsFor(catalog, view.survey.document_kinds || ["dvr"]);
  const save = () => {
    const cleaned = Object.fromEntries(Object.entries(meta).filter(([k, v]) => fields.some((f) => f.key === k) && String(v).trim()));
    return actions.data(company, cleaned).then(() => toast.success("Dati salvati"));
  };
  const pl = view.plans || {};
  return (
    <div className="space-y-4">
      {view.data_needed.length > 0 && (
        <div className="rounded-2xl bg-[#FBEFD2] p-4 text-[#8A5A00]">
          <p className="font-bold">Mancano ancora dei dati per i documenti:</p>
          <ul className="list-disc pl-5">{view.data_needed.map((d) => <li key={d.kind}><b>{d.label}</b>: {d.missing.map((m) => m.label).join(", ")}</li>)}</ul>
          <p className="mt-1 text-sm">Puoi inviare lo stesso: il consulente vedrà cosa manca.</p>
        </div>)}
      {pl.needed && pl.needed.length > 0 && (
        <div className="rounded-2xl border border-[#D9CBB4] bg-white p-4" data-testid="field-plans">
          <p className="font-bold">Planimetrie</p>
          {pl.items.length === 0
            ? <p className="text-sm text-[#5C6667]">Non serve fare nulla. Se il cliente ha la pianta (PDF, DXF o DWG), faglielo mandare{pl.address ? ` a ${pl.address}` : ""}{pl.code ? <> scrivendo nell'oggetto il codice <b className="rounded bg-[#EFE6D8] px-2 py-0.5 text-[#1E2425]">{pl.code}</b></> : " (il codice compare appena il sopralluogo è sul server)"}: il sistema la riceve e prepara da solo la bozza.</p>
            : <ul className="mt-1 list-disc pl-5 text-sm">{pl.items.map((x, i) => <li key={i}>{x.title}: {x.cad_standard ? "pronta, nello standard Artego" : x.has_pdf ? "bozza pronta (si porta da sola nello standard Artego)" : "ricevuta, in attesa di dati"}</li>)}</ul>}
        </div>)}
      <div className="rounded-2xl border border-[#D9CBB4] bg-white p-4"><p className="mb-2 font-bold">Azienda</p>
        <CompanyFields base="/field" company={company} setCompany={setCompany} /></div>
      {fields.length > 0 && (
        <div className="space-y-3 rounded-2xl border border-[#D9CBB4] bg-white p-4"><p className="font-bold">Dati dei documenti</p>
          {fields.map((f) => (
            <label key={f.key} className="block text-sm font-semibold">{f.label}
              <Input className="mt-1 h-12 border-2 text-base" disabled={!view.editable} value={meta[f.key] || ""} onChange={(e) => setMeta({ ...meta, [f.key]: e.target.value })} /></label>))}
        </div>)}
      {view.editable && <button className={PRIMARY} onClick={save}>Salva i dati</button>}
    </div>
  );
}

// ------------------------------------------------------------------ pagina
export default function FieldSurvey() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pack } = useFieldSync();
  const { view, loaded, error, actions } = useFieldSurvey(id);
  const [sheet, setSheet] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  if (error) return <p className="rounded-xl bg-[#F6E4E6] p-4 text-[#8B1E2D]">{error}</p>;
  if (!loaded || !view) return <div className="grid place-items-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const taskName = (c) => view.survey.tasks.find((t) => t.code === c)?.name || c;
  const [stateLabel, stateCls] = STATE_LABEL[view.state] || [view.state, ""];
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const onAnswer = (q, state, value, evidence) => { setEditing(null); return run(() => actions.answer(q, state, value, evidence)); };
  const onPhoto = async (q, req, file) => { try { return await actions.photo(q, req, file); } catch (e) { toast.error(e.message); return null; } };
  const send = () => run(async () => { await actions.send(); toast.success(navigator.onLine === false ? "Pronto: parte appena torna la rete." : "Inviato al consulente. Il resto lo fa l'app."); });

  const current = editing || view.next_questions[0];
  const docsToDo = view.documents.filter((d) => d.status !== "received").length;
  const dataMissing = view.data_needed.reduce((n, d) => n + d.missing.length, 0);
  const done = view.state === "inviato" || view.state === "chiuso";

  return (
    <div className="space-y-4" data-testid="field-survey">
      <button onClick={() => navigate("/campo")} className="inline-flex items-center gap-2 text-[#0D4D4F]"><ArrowLeft className="h-5 w-5" /> I tuoi sopralluoghi</button>
      <div className="rounded-2xl bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0"><h1 className="truncate font-heading text-xl font-bold">{view.survey.title}</h1><p className="truncate text-sm text-[#5C6667]">{view.survey.company_name}</p></div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${stateCls}`}>{stateLabel}</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#EFE6D8]"><i className="block h-full bg-[#0D4D4F] transition-all" style={{ width: `${view.progress.percent}%` }} /></div>
        <p className="mt-1 text-sm text-[#5C6667]">{view.progress.done} su {view.progress.required} domande · {view.progress.percent}%</p>
      </div>

      {view.local_only && <p className="rounded-2xl bg-[#FBEFD2] p-4 text-[#8A5A00]" data-testid="field-local-only">Questo sopralluogo è ancora solo sul tablet: viene aperto sul server appena c'è la rete. Intanto puoi lavorare normalmente.</p>}
      {view.problems.length > 0 && (
        <div className="rounded-2xl bg-[#F6E4E6] p-4 text-[#5b1019]" data-testid="field-problems">
          <p className="font-bold">Avvisi dalla sincronizzazione</p>
          <ul className="list-disc pl-5">{view.problems.map((p, i) => <li key={i}>{p.message}</li>)}</ul>
          <button className="mt-2 text-sm underline" onClick={() => actions.clearProblems()}>Ho letto, nascondi</button>
        </div>)}
      {view.change_request && <p className="rounded-2xl bg-[#F6E4E6] p-4 text-[#5b1019]"><b>Il consulente chiede:</b> {view.change_request}</p>}
      {view.state === "da_completare" && (
        <p className="flex gap-3 rounded-2xl bg-[#FBEFD2] p-4 text-[#8A5A00]"><AlertTriangle className="h-6 w-6 shrink-0" />
          <span>Il sopralluogo è stato inviato ma manca ancora qualcosa{view.missing_blocking_documents.length ? `: ${view.missing_blocking_documents.map((d) => d.name).join("; ")}` : ""}. Completa e invia di nuovo.</span></p>)}

      {done ? (
        <div className="rounded-3xl border border-[#1F6B45] bg-[#DDF0E5] p-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#1F6B45]" />
          <h2 className="mt-2 text-2xl font-bold">{view.state === "inviato" ? "Inviato al consulente" : "Sopralluogo chiuso"}</h2>
          <p className="mt-1">{view.state === "inviato" ? "L'app sta lavorando i dati e prepara il documento. Il consulente lo controlla. Non devi fare altro." : "Non è più modificabile."}</p>
        </div>
      ) : view.state === "in_coda" ? (
        <div className="rounded-3xl border border-[#8A5A00] bg-[#FBEFD2] p-6 text-center" data-testid="field-queued">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[#8A5A00]" />
          <h2 className="mt-2 text-2xl font-bold">Pronto, in attesa di rete</h2>
          <p className="mt-1">Tutto è salvato sul tablet. Appena c'è internet i dati partono da soli verso la piattaforma: non devi fare altro.</p>
        </div>
      ) : current ? (
        <FieldQuestion q={current} taskName={taskName} busy={busy} onAnswer={onAnswer} onPhoto={onPhoto} editing={!!editing} onCancel={() => setEditing(null)} />
      ) : (
        <div className="rounded-3xl border border-[#D9CBB4] bg-white p-6">
          <CheckCircle2 className="h-10 w-10 text-[#1F6B45]" />
          <h2 className="mt-2 text-2xl font-bold">Hai risposto a tutte le domande</h2>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-lg">
            <li>{view.open_items.length} dati non disponibili (verranno segnalati al consulente)</li>
            <li>{view.missing_evidence.length} risposte senza la prova richiesta</li>
            <li>{docsToDo} documenti ancora da acquisire</li>
          </ul>
          <button className={`${PRIMARY} mt-5 inline-flex w-full items-center justify-center gap-2 sm:w-auto`} disabled={busy || !view.can_send} onClick={send} data-testid="field-send"><Send className="h-5 w-5" /> Invia al consulente</button>
        </div>)}

      {sheet && (
        <div className="fixed inset-0 z-30 flex flex-col bg-[#FAF6EE]">
          <div className="flex items-center justify-between border-b border-[#D9CBB4] bg-white px-5 py-4"><h2 className="text-xl font-bold">{{ data: "Dati azienda e documenti", docs: "Documenti", units: "Esemplari", answers: "Risposte date" }[sheet]}</h2>
            <button className={`${SECOND} min-h-[48px]`} onClick={() => setSheet(null)}>Chiudi</button></div>
          <div className="flex-1 overflow-auto p-5">
            {sheet === "data" && <DataSheet view={view} actions={actions} catalog={pack.catalog} />}
            {sheet === "docs" && <DocsSheet view={view} actions={actions} />}
            {sheet === "units" && <UnitsSheet view={view} actions={actions} />}
            {sheet === "answers" && <AnswersSheet view={view} onEdit={(a) => { setEditing(a); setSheet(null); }} />}
          </div>
        </div>)}

      <div className="fixed inset-x-0 bottom-0 z-20 flex flex-wrap items-center gap-2 border-t border-[#D9CBB4] bg-white px-3 py-2">
        <button className={`${SECOND} flex min-h-[52px] items-center gap-2 px-4 text-base`} onClick={() => setSheet("data")}><Building2 className="h-5 w-5" /> Dati{dataMissing > 0 ? ` (${dataMissing})` : ""}</button>
        <button className={`${SECOND} flex min-h-[52px] items-center gap-2 px-4 text-base`} onClick={() => setSheet("docs")}><FileText className="h-5 w-5" /> Documenti ({docsToDo})</button>
        {view.equipment_instances.length > 0 && <button className={`${SECOND} flex min-h-[52px] items-center gap-2 px-4 text-base`} onClick={() => setSheet("units")}><Wrench className="h-5 w-5" /> Esemplari</button>}
        <button className={`${SECOND} flex min-h-[52px] items-center gap-2 px-4 text-base`} onClick={() => setSheet("answers")}><ListChecks className="h-5 w-5" /> Risposte ({view.answered.length})</button>
        {busy && <Loader2 className="ml-auto h-5 w-5 animate-spin" />}
      </div>
    </div>
  );
}
